import type React from "react"
import { useEffect, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { auth, db } from "@utils/firebase"
import { useGetUser } from "@utils/hooks"
import { doc, updateDoc, serverTimestamp, getDoc } from "firebase/firestore"
import Navbar from "@components/layout/Navbar"
import Footer from "@components/layout/Footer"
import Loading from "@components/layout/Loading"
import { Link, Navigate } from "react-router-dom"
import Avatar from "@components/Avatar/Avatar"
import { ReactComponent as PennyLogo } from "../../assets/icons/penny-icon.svg"
import { ReactComponent as InsuranceLogo } from "../../assets/icons/insurance-logo.svg"
import { ReactComponent as Stripe } from "../../assets/icons/Frame 26087128.svg"
import { ReactComponent as Advisor } from "../../assets/icons/Frame 26087123.svg"
import PennyInsights from "./PennyInsights"
import ImageWithFallback from "@components/Image/ImageWithFallback"
import PennyMobileView from "@assets/illustrations/penny_mobile_view.webp"
import SectionHeader from "@components/Section/SectionHeader"
import { twMerge } from "tailwind-merge"
import { ReactComponent as MinusIcon } from "@assets/icons/minus.svg"
import { ReactComponent as PlusIcon } from "@assets/icons/plus.svg"
import { ReactComponent as EditIcon } from "@assets/icons/edit.svg"
import { ReactComponent as CheckedIcon } from "@assets/icons/checked.svg"
import { Collapse } from "@mantine/core"

type PensionData = {
  birthday: string
  current_employer: string
  company_eircode: string
  current_salary: string
  normal_retirement_age: string
  current_fund_value: string
  monthly_contributions: string
  employer_contributions: string
  standard_prsa: string
  policy_number: string
  start_date: string
  risk_profile: string
  my_advisor: string
  financial_advisor: string
  contact_details: string
  createdAt: any
}

// Add type for user data
type UserData = {
  birth_date?: string
  address_line_1?: string
  address_line_2?: string
  address_type?: string
  annual_salary?: number
  fund_value?: number
  best_match?: string
  cert_assign?: string
  city?: string
  company_eircode?: string
  country?: string
  country_of_nationality?: string
  current_employer?: string
  PRSA_approval?: string
  policy_number?: string
  pension_start_date?: string
  risk_type?: string
  retire_age?: number
  pension_contribution_value?: number
  employer_pension_contribution?: number
  createdAt?: any
  uid?: string
  updatedAt?: any
}

const PennySpace: React.FC = () => {
  const [user] = useAuthState(auth)
  const { data: userData, isLoading: userDataLoading } = useGetUser()
  const [pensionData, setPensionData] = useState<PensionData | null>(null)
  const [activeTab, setActiveTab] = useState("Penny")
  const [isLoading, setIsLoading] = useState(true)
  const [updatedUserData, setUpdatedUserData] = useState<UserData | null>(null)

  const [oneq, setOneq] = useState(false)
  const [twoq, setTwoq] = useState(false)
  const [threeq, setThreeq] = useState(false)
  const [fourq, setFourq] = useState(false)
  const [fiveq, setFiveq] = useState(false)

  const [isEditingEmployer, setIsEditingEmployer] = useState(false)
  const [newEmployer, setNewEmployer] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [employerError, setEmployerError] = useState("")

  // Add toast state
  const [showToast, setShowToast] = useState(false)

  // Add capitalizeName function
  const capitalizeName = (name: string) => {
    if (!name) return ""
    return name
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  }

  useEffect(() => {
    const fetchPensionData = async () => {
      if (!user) return

      try {
        // Fetch pension data from Firestore using UID as document ID
        const userUid = user.uid
        if (!userUid) return

        // Get document where document ID is the user's UID
        const userDocRef = doc(db, "users", userUid)
        const userDocSnapshot = await getDoc(userDocRef)

        // Check if document exists
        if (!userDocSnapshot.exists()) {
          console.log("No document found with this UID")
          return
        }

        console.log(userDocSnapshot)

        // Process the document
        const data = userDocSnapshot.data()
        setPensionData({
          birthday: data.birth_date || "--",
          current_employer: data.company_eircode || "--",
          company_eircode: data.company_eircode || "--",
          current_salary: `€${data.annual_salary?.toLocaleString() || "--"}`,
          normal_retirement_age: data.retire_age?.toString() || "--",
          current_fund_value: `€${data.fund_value?.toLocaleString() || "--"}`,
          monthly_contributions: `€${data.pension_contribution_value?.toLocaleString() || "--"}`,
          employer_contributions: `€${data.employer_pension_contribution?.toLocaleString() || "--"}`,
          standard_prsa: data.PRSA_approval || "Default Annuity Investment Strategy",
          policy_number: data.policy_number || "--",
          start_date: data.pension_start_date || "--",
          risk_profile: data.risk_type || "--",
          my_advisor: "Penny Life",
          financial_advisor: data.financial_advisor || "Lesley Tully",
          contact_details: data.contact_details || "lesley@thisispenny.com",
          createdAt: data.createdAt,
        })

        // Update userData state to include all fields from the document
        if (userData) {
          // Create a new object with merged data
          const mergedUserData = {
            ...userData,
            birth_date: data.birth_date,
            address_line_1: data.address_line_1,
            address_line_2: data.address_line_2,
            address_type: data.address_type,
            annual_salary: data.annual_salary,
            fund_value: data.fund_value,
            best_match: data.best_match,
            cert_assign: data.cert_assign,
            city: data.city,
            company_eircode: data.company_eircode,
            country: data.country,
            country_of_nationality: data.country_of_nationality,
            current_employer: data.current_employer || data.company_eircode,
            PRSA_approval: data.PRSA_approval,
            policy_number: data.policy_number,
            pension_start_date: data.pension_start_date,
            risk_type: data.risk_type,
            retire_age: data.retire_age,
            pension_contribution_value: data.pension_contribution_value,
            employer_pension_contribution: data.employer_pension_contribution,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          }
          // Set the updated user data
          setUpdatedUserData(mergedUserData)
        }
      } catch (error) {
        console.error("Error fetching pension data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    if (!userDataLoading && userData) {
      fetchPensionData()
    }
  }, [user, userData, userDataLoading])

  const copyEmail = () => {
    navigator.clipboard.writeText("lesley@thisispenny.com")
    // Show toast notification
    setShowToast(true)
    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      setShowToast(false)
    }, 3000)
  }

  const sendEmail = () => {
    window.location.href = "mailto:lesley@thisispenny.com"
  }

  // Format date helper function that handles different timestamp formats
  const formatDate = (timestamp: any) => {
    let date

    if (timestamp && typeof timestamp === "object") {
      // Handle Firestore Timestamp
      if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000)
      }
      // Handle JavaScript Date object
      else if (timestamp instanceof Date) {
        date = timestamp
      }
      // Handle objects with toDate() method (like Firestore Timestamp)
      else if (typeof timestamp.toDate === "function") {
        date = timestamp.toDate()
      }
    }
    // Handle numeric timestamp
    else if (typeof timestamp === "number") {
      date = new Date(timestamp)
    }

    // Return formatted date or fallback
    return date instanceof Date && !isNaN(date.getTime())
      ? date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
      : ""
  }

  // Update the effect to use proper typing
  useEffect(() => {
    if (isEditingEmployer && updatedUserData) {
      setNewEmployer(updatedUserData.current_employer || updatedUserData.company_eircode || "")
      setEmployerError("") // Clear any previous error when starting to edit
    }
  }, [isEditingEmployer, updatedUserData])

  // Update the handleEmployerUpdate function
  const handleEmployerUpdate = async () => {
    if (!user) return

    // Clear previous error
    setEmployerError("")

    // Validate input
    if (!newEmployer.trim()) {
      setEmployerError("Current Employer cannot be empty")
      return
    }

    setIsSaving(true)
    try {
      const userUid = user.uid
      const userDocRef = doc(db, "users", userUid)
      
      await updateDoc(userDocRef, {
        current_employer: newEmployer.trim(),
        company_eircode: newEmployer.trim(),
        updatedAt: serverTimestamp()
      })

      // Update local state with proper typing
      setUpdatedUserData((prev: UserData | null) => {
        if (!prev) return null
        return {
          ...prev,
          current_employer: newEmployer.trim(),
          company_eircode: newEmployer.trim(),
          updatedAt: new Date()
        }
      })
      
      setIsEditingEmployer(false)
    } catch (error) {
      console.error("Error updating employer:", error)
      setEmployerError("Failed to update employer. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  if (!user) {
    return <Navigate to="/login" />
  }

  if (isLoading || userDataLoading) {
    return <Loading />
  }

  // Use updatedUserData if available, otherwise fall back to userData
  const displayData = updatedUserData || userData

  return (
    <div className="flex flex-col min-h-screen bg-pn_paper-1">
      <Navbar />
      
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-16 md:top-20 lg:top-24 left-1/2 transform -translate-x-1/2 z-[100] w-[90%] max-w-sm sm:max-w-md mx-auto">
          <div 
            className="bg-white border border-pn_gray-200 rounded-lg shadow-lg p-3 sm:p-4 flex items-center gap-3 transition-all duration-300 ease-out"
            style={{
              animation: showToast ? 'slideInFromTop 0.3s ease-out' : 'slideOutToTop 0.3s ease-in'
            }}
          >
            <div className="bg-pn_green-50 rounded-full p-1.5 flex-shrink-0">
              <CheckedIcon width={16} height={16} className="text-pn_green-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-pn-body-regular text-pn_gray-950 font-medium">
                Email copied to clipboard!
              </p>
            </div>
            <button
              onClick={() => setShowToast(false)}
              className="text-pn_gray-400 hover:text-pn_gray-600 p-1 flex-shrink-0 transition-colors duration-200"
              aria-label="Close notification"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideOutToTop {
            from {
              opacity: 1;
              transform: translateY(0);
            }
            to {
              opacity: 0;
              transform: translateY(-20px);
            }
          }
        `
      }} />

      <div className="flex-1 px-4 md:px-6 lg:px-[80px] py-6">
        <h1 className="text-pn-large-title mb-4 md:mb-6">
          {activeTab === "Penny" || activeTab === "New Ireland" ? "My Pensions" : "My Pensions"}
        </h1>

        {/* Tabs */}
        {/* <div className="flex border-b border-pn_gray-200 mb-4 md:mb-6 overflow-x-auto">
          <button
            className={`pb-2 px-3 md:px-4 whitespace-nowrap ${activeTab === "Penny" ? "border-b-2 border-pn_black font-semibold" : "text-pn_gray-600"
              }`}
            onClick={() => setActiveTab("Penny")}
          >
            Penny
          </button>
          <button
            className={`pb-2 px-3 md:px-4 whitespace-nowrap ${activeTab === "Zurich Life" ? "border-b-2 border-pn_black font-semibold" : "text-pn_gray-600"
              }`}
            onClick={() => setActiveTab("Zurich Life")}
          >
            Zurich Life
          </button>
          <button
            className={`pb-2 px-3 md:px-4 whitespace-nowrap ${activeTab === "New Ireland" ? "border-b-2 border-pn_black font-semibold" : "text-pn_gray-600"
              }`}
            onClick={() => setActiveTab("New Ireland")}
          >
            New Ireland
          </button>
        </div> */}

        {/* Pension Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
          {/* Personal Details Card */}
          <div className="bg-white rounded-2xl border border-pn_gray-200 overflow-hidden h-full">
            <div className="bg-pn_paper-3 p-4">
              <div className="bg-pn_black text-white px-2 py-1 rounded-md text-pn-callout-emphasis inline-block">
                Personal Details
              </div>
              <div className="mt-4 flex justify-between items-center">
                <div>
                  <p className="text-pn-callout-regular text-pn_gray-600">Name</p>
                  <p className="text-pn-body-large-emphasis">
                    {user.displayName ? <span className="font-eightiescomeback text-[22px] leading-[22px] font-normal">{capitalizeName(user.displayName)}</span> : ""}
                  </p>
                </div>
                {user?.emailVerified && <Avatar size="small" user={user} />}
              </div>
              <div className="mt-4">
                <p className="text-pn-callout-regular text-pn_gray-600">Birthday</p>
                <p className="text-pn-body-regular">{displayData?.birth_date || "--"}</p>
              </div>
            </div>
            <div className="p-4">
              <div className="mt-2 flex items-center justify-between border-b-2 border-dashed pb-4">
                <div className="flex-1">
                  <p className="text-pn-callout-regular text-pn_gray-600">Current Employer</p>
                  {isEditingEmployer ? (
                    <div className="flex flex-col gap-2 mt-1">
                      <input
                        type="text"
                        value={newEmployer}
                        onChange={(e) => {
                          // Only allow letters and spaces
                          const filteredValue = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                          setNewEmployer(filteredValue);
                        }}
                        className="text-pn-body-regular border border-pn_gray-200 rounded px-2 py-1 focus:outline-none focus:border-pn_cyan-500 w-full"
                        placeholder="Enter employer name"
                        maxLength={30}
                      />
                      {employerError && <p className="text-red-500 text-sm mt-1">{employerError}</p>}
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={handleEmployerUpdate}
                          disabled={isSaving}
                          className="bg-[#F4E34E] text-pn_gray-950 rounded-full py-1 px-3 text-pn-button-2 disabled:opacity-50 whitespace-nowrap"
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingEmployer(false)
                            setEmployerError("") // Clear error when canceling
                          }}
                          className="bg-pn_gray-200 text-pn_gray-950 rounded-full py-1 px-3 text-pn-button-2 whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-pn-body-regular">
                      {displayData?.current_employer || displayData?.company_eircode || "--"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!isEditingEmployer && (
                    <button
                      onClick={() => setIsEditingEmployer(true)}
                      className="bg-pn_gray-200 text-pn_gray-950 rounded-full p-2 hover:bg-pn_gray-300 transition-colors"
                      aria-label="Edit employer"
                    >
                      <EditIcon width={16} height={16} />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-pn-callout-regular text-pn_gray-600">Current Salary</p>
                  <p className="text-pn-body-regular">{displayData?.annual_salary?.toLocaleString() || "--"}</p>
                </div>
                <div>
                  <p className="text-pn-callout-regular text-pn_gray-600">Normal Retirement Age</p>
                  <p className="text-pn-body-regular">{displayData?.retire_age || "--"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pension Details Card */}
          <div className="bg-white rounded-2xl border border-pn_gray-200 overflow-hidden h-full">
            <div className="bg-pn_paper-3 p-4">
              <div className="bg-pn_black text-white px-2 py-1 rounded-md text-pn-callout-emphasis inline-block">
                Pension Details
              </div>
              <div className="mt-4 flex flex-col md:flex-row justify-between gap-3 md:gap-0">
                <div>
                  <p className="text-pn-callout-regular text-pn_gray-600">
                    {activeTab === "Zurich Life" ? "Fund Value" : "Current Fund Value"}
                  </p>
                  <p className="text-pn-body-large-emphasis">
                    {displayData?.fund_value ? `€${displayData.fund_value.toLocaleString()}` : "€--"}
                  </p>
                </div>
                {/* <div className="flex items-center gap-2 md:gap-4 lg:gap-9 border border-pn_gray-200 rounded-lg p-1 md:p-1.5">
                  <div>
                    <p className="text-pn-callout-regular text-pn_gray-600">
                      {activeTab === "Zurich Life" ? "Past Employer" : ""}
                    </p>
                    <p className="text-pn-body-large-emphasis">{activeTab === "Zurich Life" ? "Stripe Lcc" : ""}</p>
                  </div>
                  <div>{activeTab === "Zurich Life" ? <Stripe width={50} height={41} /> : ""}</div>
                </div> */}
              </div>
              <div className={`grid grid-cols-2 gap-4 ${activeTab === "Zurich Life" ? "mt-[14px]" : "pb-3 mt-4"}`}>
                <div>
                  <p className="text-pn-callout-regular text-pn_gray-600">
                    {activeTab === "Zurich Life" ? "My Contributions" : "Monthly Contributions"}
                  </p>
                  <p className="text-pn-body-regular">
                    {displayData?.pension_contribution_value ? `€${displayData.pension_contribution_value}` : "€--"}
                  </p>
                </div>
                <div>
                  <p className="text-pn-callout-regular text-pn_gray-600">Employer Contributions</p>
                  <p className="text-pn-body-regular">
                    {displayData?.employer_pension_contribution
                      ? `€${displayData.employer_pension_contribution}`
                      : "€--"}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="mt-2 flex items-center justify-between border-b-2 border-dashed pb-4">
                <div>
                  <p className="text-pn-callout-regular text-pn_gray-600">Standard PRSA(Zurich)</p>
                  <p className="text-pn-body-regular">
                    {displayData?.PRSA_approval || "Default Annuity Investment Strategy"}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <InsuranceLogo width={50} height={41} />
                </div>
              </div>
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="min-w-0">
                <p className="text-pn-callout-regular text-pn_gray-600">Policy Number</p>
                <p className="text-pn-body-regular break-words overflow-hidden">
                  {displayData?.policy_number || "--"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-pn-callout-regular text-pn_gray-600">Start Date</p>
                <p className="text-pn-body-regular break-words overflow-hidden">
                  {displayData?.pension_start_date || "--"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-pn-callout-regular text-pn_gray-600">Risk Profile</p>
                <p className="text-pn-body-regular break-words overflow-hidden">
                  {displayData?.risk_type || "--"}
                </p>
              </div>
            </div>
            </div>
          </div>

          {/* Support Details Card */}
          <div className="bg-white rounded-2xl border border-pn_gray-200 overflow-hidden md:col-span-2 lg:col-span-1 h-full">
            <div className="bg-pn_paper-3 pl-4 pt-4">
              <div className="bg-pn_black text-white px-2 py-1 rounded-md text-pn-callout-emphasis inline-block">
                Support Details
              </div>
              <div className="flex flex-col md:flex-row justify-between">
                <div className="mt-2 items-center grid grid-cols-1">
                  <PennyLogo width={50} height={41} />
                  <div>
                    <p className="text-pn-callout-regular text-pn_gray-600">My Advisor</p>
                    <div className="flex flex-col">
                      <p className="text-3xl">
                        <span className="font-eightiescomeback text-[22px] leading-[22px] font-normal">Penny Life</span>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="relative flex justify-end mt-4 md:mt-0">
                  <div className="w-full md:w-auto">
                    <Advisor width={250} height={140} className="w-full md:w-auto" />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div>
                <div className="mt-2 items-center border-b-2 border-dashed pb-4">
                  <p className="text-pn-callout-regular text-pn_gray-600">Financial Advisor</p>
                  <p className="text-pn-body-regular">Lesley Tully</p>
                </div>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <div className="mt-0 sm:mt-4">
                  <p className="text-pn-callout-regular text-pn_gray-600">Contact Details</p>
                  <p className="text-pn-body-regular">lesley@thisispenny.com</p>
                </div>
                <div className="flex gap-3 md:gap-4 mt-2 sm:mt-0">
                  <button
                    onClick={copyEmail}
                    className="bg-pn_gray-200 text-pn_gray-950 rounded-full p-2"
                    aria-label="Copy email"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M8 5H6C4.89543 5 4 5.89543 4 7V19C4 20.1046 4.89543 21 6 21H16C17.1046 21 18 20.1046 18 19V17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 9H6C4.89543 9 4 9.89543 4 11V19C4 20.1046 4.89543 21 6 21H16C17.1046 21 18 20.1046 18 19V17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 5H16C17.1046 5 18 5.89543 18 7V13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={sendEmail}
                    className="bg-[#F4E34E] text-pn_gray-950 rounded-full py-2 px-4 text-pn-button-2"
                  >
                    Send Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 text-pn-callout-regular text-pn_gray-600 pb-4">
          {displayData?.updatedAt ? `Last Updated on ${formatDate(displayData.updatedAt)}` : ""}
        </div>
      </div>

      {/* Add the PennyInsights component here */}
      <PennyInsights />

      <div className="py-4 md:py-[80px] px-4 lg:px-[80px]">
        <div className="bg-pn_paper-3 border border-pn_gray-200 rounded-2xl flex items-center justify-center gap-x-[10%] pt-[32px] pb-[48px] md:pt-0 md:pb-0 px-6 md:px-[5%]">
          <div className="max-w-[439px] w-full pt-[68px] hidden md:block">
            <ImageWithFallback
              src={PennyMobileView || "/placeholder.svg"}
              alt="penny_mobile_friendly"
              className="w-full h-full"
              width="100%"
              height="auto"
              loading="lazy"
            />
          </div>
          <div className="flex flex-col gap-y-[18px] max-w-full md:max-w-[452px] w-full">
            <div className="bg-pn_black rounded-lg py-1 px-2 w-fit">
              <span className="text-pn_white text-pn-callout-emphasis">Go Beyond</span>
            </div>
            <div className="flex flex-col gap-y-4">
              <span className="text-pn-large-title text-pn_gray-950">
                Come as you are, as you were As you want it to be
              </span>
              <span className="text-pn-body-large-regular text-pn_gray-950">
                Penny is available everywhere. You can start here, continue there, and be anywhere.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-[95px] md:pb-[80px] flex flex-col gap-y-0 lg:gap-y-6">
        <SectionHeader label="We're here to help" />

        <div className="px-4 lg:px-[80px] w-full flex flex-col">
          <div className="border-b-2 border-pn_gray-200 flex-col items-center w-full">
            <button
              onClick={() => setOneq((prev) => !prev)}
              className="justify-between flex py-6 w-full items-center gap-x-6"
            >
              <span className="text-pn-title-regular text-left md:text-pn-large-title text-pn_gray-950 flex-1">
                What is Penny?
              </span>
              <div
                className={twMerge(
                  oneq ? "bg-pn_gray-200" : "bg-[#F4E34E]",
                  "w-10 h-10 rounded-full flex items-center justify-center",
                )}
              >
                {oneq ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
              </div>
            </button>

            <Collapse in={oneq}>
              <div className="max-w-[845px] pb-10 w-full text-pn_gray-950 text-pn-body-large-regular">
                <span>
                  Penny is a 100% online platform that connects customers with the pension products and financial advice
                  they need. Through our simple online process you can apply, view and manage your pension, all from the
                  comfort of your own phone.
                </span>
              </div>
            </Collapse>
          </div>

          <div className="border-b-2 border-pn_gray-200 flex-col items-center w-full">
            <button
              onClick={() => setTwoq((prev) => !prev)}
              className="justify-between flex py-6 w-full items-center gap-x-6"
            >
              <span className="text-pn-title-regular text-left md:text-pn-large-title text-pn_gray-950 flex-1">
                How do I get started with Penny?
              </span>
              <div
                className={twMerge(
                  twoq ? "bg-pn_gray-200" : "bg-[#F4E34E]",
                  "w-10 h-10 rounded-full flex items-center justify-center",
                )}
              >
                {twoq ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
              </div>
            </button>

            <Collapse in={twoq}>
              <div className="max-w-[845px] pb-10 w-full text-pn_gray-950 text-pn-body-large-regular">
                <span>
                  Signing up with Penny is easy - just create an account, provide some basic details and receive your
                  recommendation. You can pick from a range of pension plans, adjust as needed and get regular check-ins
                  with your personal financial advisor.
                </span>
              </div>
            </Collapse>
          </div>

          <div className="border-b-2 border-pn_gray-200 flex-col items-center w-full">
            <button
              onClick={() => setThreeq((prev) => !prev)}
              className="justify-between flex py-6 w-full items-center gap-x-6"
            >
              <span className="text-pn-title-regular text-left md:text-pn-large-title text-pn_gray-950 flex-1">
                How long does it take complete?
              </span>
              <div
                className={twMerge(
                  threeq ? "bg-pn_gray-200" : "bg-[#F4E34E]",
                  "w-10 h-10 rounded-full flex items-center justify-center",
                )}
              >
                {threeq ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
              </div>
            </button>

            <Collapse in={threeq}>
              <div className="max-w-[845px] pb-10 w-full text-pn_gray-950 text-pn-body-large-regular">
                <span>
                  Your customised pension will be ready within 24 hours, and once selected, we'll have your pension up
                  and running in two days.
                </span>
              </div>
            </Collapse>
          </div>

          <div className="border-b-2 border-pn_gray-200 flex-col items-center w-full">
            <button
              onClick={() => setFourq((prev) => !prev)}
              className="justify-between flex py-6 w-full items-center gap-x-6"
            >
              <span className="text-pn-title-regular text-left md:text-pn-large-title text-pn_gray-950 flex-1">
                What documents are necessary?
              </span>
              <div
                className={twMerge(
                  fourq ? "bg-pn_gray-200" : "bg-[#F4E34E]",
                  "w-10 h-10 rounded-full flex items-center justify-center",
                )}
              >
                {fourq ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
              </div>
            </button>

            <Collapse in={fourq}>
              <div className="max-w-[845px] pb-10 w-full text-pn_gray-950 text-pn-body-large-regular">
                <span>
                  Once your pension recommendation is confirmed, you'll be prompted to upload two documents: proof of
                  PPSN (Personal Public Service Number) and proof of identity (i.e. Passport, Drivers Licence, National
                  Identity can). <br /> <br /> To finalise the process, simply review, sign and submit to have your
                  pension processed.
                </span>
              </div>
            </Collapse>
          </div>

          <div className="border-b-2 border-pn_gray-200 flex-col items-center w-full">
            <button
              onClick={() => setFiveq((prev) => !prev)}
              className="justify-between flex py-6 w-full items-center gap-x-6"
            >
              <span className="text-pn-title-regular text-left md:text-pn-large-title text-pn_gray-950 flex-1">
                Is Penny secure?
              </span>
              <div
                className={twMerge(
                  fiveq ? "bg-pn_gray-200" : "bg-[#F4E34E]",
                  "w-10 h-10 rounded-full flex items-center justify-center",
                )}
              >
                {fiveq ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
              </div>
            </button>

            <Collapse in={fiveq}>
              <div className="max-w-[845px] pb-10 w-full text-pn_gray-950 text-pn-body-large-regular">
                <span>
                  Penny takes your security very seriously. We are triple regulated -by the Central Bank of Ireland, the
                  Pensions Authority of Ireland and each of our pension partners' products. We follow information
                  security best practices, overseen by our Chief of Compliance to confirm what we're doing is effective
                  Need more help? We get it: you've got more questions and now you need a human. Contact us at{" "}
                  <Link to={"mailto:hey@iam-penny.com"} className="text-pn-body-large-link text-pn_cyan-500">
                    hey@iam-penny.com
                  </Link>{" "}
                  or call us on{" "}
                  <Link to="tel:+335879119440" className="text-pn-body-large-link text-pn_cyan-500">
                    +335 87 9119 440
                  </Link>{" "}
                  (a real human will answer)
                </span>
              </div>
            </Collapse>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default PennySpace
